import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Capacitor } from '@capacitor/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import urljoin from 'url-join';

import { APIService } from 'src/app/services/api.service';
import { ImageUploadService } from 'src/app/services/profile/image-upload.service';
import { Notification, User, UserRole } from 'src/app/models/user';
import { AlertService } from 'src/app/services/alert.service';
import { Storage } from '@capacitor/storage';

interface UserLoginSuccessResponse {
  user: User;
  token: string;
}

interface ConsentResponse {
  detail: string;
}

@Injectable({
  providedIn: 'root',
})
export class UserService extends APIService {
  public static JWT_KEY = 'UserJWT';
  public static IMG_KEY = 'ProfileImage';
  public static LANG_KEY = 'UserLanguage';
  public readonly authState$ = new BehaviorSubject<boolean>(false);
  public readonly onUpdate$ = new Subject<void>();
  notificationsUrl = urljoin(this.BASE_URL, 'notifications/me/');
  countries: { [key: string]: string };
  states: { [key: string]: string };

  constructor(
    private http: HttpClient,
    private jwtHelper: JwtHelperService,
    private imgUploadService: ImageUploadService,
    private alertService: AlertService
  ) {
    super();
    this.isAuthenticated.then((yes) => this.authState$.next(yes));
  }

  public get isAuthenticated() {
    return (async () => {
      const token = await this.authToken;
      return !!token && !this.jwtHelper.isTokenExpired(token);
    })();
  }

  public get authToken() {
    return (async () =>
      (await Storage.get({ key: UserService.JWT_KEY })).value)();
  }

  public get userId() {
    return (async () => {
      const token = await this.authToken;
      const decoded = this.jwtHelper.decodeToken(token);
      return (decoded || { user_id: null }).user_id;
    })();
  }

  public get authUserRole() {
    return (async () => {
      const token = await this.authToken;
      const decoded = this.jwtHelper.decodeToken(token);
      return (decoded || { role: null }).role as string;
    })();
  }

  public async logout() {
    await Storage.remove({ key: UserService.JWT_KEY });
    if (Capacitor.isNative) {
      (window as any).plugins.OneSignal.removeExternalUserId();
    }
    this.authState$.next(false);
  }

  public async login(email: string, password: string) {
    const body = { email, password };
    const url = urljoin(this.BASE_URL, 'rest-auth/login/');
    const { user, token } = await this.http
      .post<{ user: User; token: string }>(url, body)
      .toPromise();
    await Storage.set({ key: UserService.JWT_KEY, value: token });
    this.setProfileImage(user?.image?.SQUARE);
    if (Capacitor.isNativePlatform()) {
      (window as any).plugins.OneSignal.setExternalUserId(user.identifier);
    }
    this.authState$.next(true);
    return user;
  }

  public async signup(
    email: string,
    password: string,
    birthDate: string,
    role: UserRole = 'player',
    sport: string,
    parentEmail?: string
  ) {
    const url = urljoin(this.BASE_URL, 'rest-auth/registration/');
    const body: { [key: string]: any } = {
      email,
      password,
      role,
      sport,
      birth_date: birthDate,
      parent_email: '',
    };

    const { user, token } = await this.http
      .post<UserLoginSuccessResponse>(url, body)
      .toPromise();
    if (Capacitor.isNativePlatform()) {
      (window as any).plugins.OneSignal.setExternalUserId(user.identifier);
    }
    this.authState$.next(true);
    await Storage.set({ key: UserService.JWT_KEY, value: token });
    return user;
  }

  public async getUser(id: string | number = 'me') {
    const url = urljoin(this.BASE_URL, `users/${id}/`);
    return this.http
      .get<User>(url)
      .pipe(
        tap((user) => {
          this.imgUploadService.changeProfilePhoto(user?.image?.SQUARE);
        })
      )
      .toPromise();
  }

  public async getOtherUser(id: string) {
    const url = urljoin(this.BASE_URL, `users/${id}/`);
    return this.http.get<User>(url).toPromise();
  }

  public async getNotifications() {
    return this.http.get<Notification[]>(this.notificationsUrl).toPromise();
  }

  public async markNotificationAsRead(notificationId: number) {
    const url = urljoin(this.notificationsUrl, `${notificationId}/`);
    return this.http
      .patch<Notification>(url, { is_read: true })
      .toPromise();
  }

  public async markNotificationsAsSeen(ids: number[]) {
    const url = urljoin(this.notificationsUrl, 'seen/');
    return this.http
      .patch<Notification[]>(url, { ids })
      .toPromise();
  }

  public async getCountries() {
    if (!this.countries) {
      this.countries = await this.http
        .get<{ [key: string]: string }>('/assets/data/countries.json')
        .toPromise();
    }
    return this.countries;
  }

  public async getGermanStates() {
    if (!this.states) {
      this.states = await this.http
        .get<{ [key: string]: string }>('/assets/data/german-states.json')
        .toPromise();
    }
    return this.states;
  }

  setProfileImage(image: string) {
    if (image) {
      this.imgUploadService.changeProfilePhoto(image);
    }
  }

  editUser(body: User, notifyListeners = true) {
    const url = urljoin(this.BASE_URL, 'users/me/');
    return this.http
      .patch(url, body)
      .pipe(
        tap(() => {
          if (notifyListeners) {
            this.onUpdate$.next();
          }
        })
      )
      .toPromise();
  }

  resetPassword(email: string) {
    const url = urljoin(this.BASE_URL, 'rest-auth/password/reset/');
    return this.http
      .post<{ detail: string }>(url, { email })
      .toPromise();
  }

  confirmResetPassword(uid: string, token: string, password: string) {
    const url = urljoin(this.BASE_URL, 'rest-auth/password/reset/confirm/');
    return this.http
      .post<{ detail: string }>(url, {
        uid,
        token,
        new_password1: password,
        new_password2: password,
      })
      .toPromise();
  }

  public requestWerderBremenConsent() {
    const url = urljoin(this.BASE_URL, 'users/consent/request/werder-bremen/');
    return this.http.post(url, {}).toPromise();
  }

  public async deleteAccount(userId: number) {
    const url = urljoin(this.BASE_URL, `users/delete-user/${userId}/`);
    return this.http
      .post(url, {}, { observe: 'response' })
      .pipe(
        map((res) => {
          return res;
        })
      )
      .toPromise();
  }
}
